import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SPECTRUM_BANDS = 16;
const LED_BLUE = "#3b82f6";
const MOUTH_LED = "#facc15";

const Robot = ({ isSpeaking = false, speechSpectrum = null, size = 'large', emotion = 'happy' }) => {
  const sizeConfig = {
    small: { width: 100, height: 105 },
    medium: { width: 150, height: 155 },
    large: { width: 220, height: 230 }
  };

  const { width, height } = sizeConfig[size];

  // LED eyes made of horizontal lines (not solid blobs)
  const generateEyeLines = (centerX, centerY, tilt = 0) => {
    const lines = [];
    const eyeWidth = 16;
    const lineCount = 6;
    const lineHeight = 2.1;
    const gap = 1;

    const totalHeight = lineCount * (lineHeight + gap) - gap;
    const startY = centerY - totalHeight / 2;

    for (let i = 0; i < lineCount; i++) {
      const progress = i / (lineCount - 1);
      const curve = Math.sin(progress * Math.PI);
      const w = eyeWidth * (0.35 + 0.65 * curve);
      const opacity = 0.45 + 0.55 * curve;

      // Keep each LED bar horizontal, but shift rows progressively to create
      // an overall "rotated eye" silhouette.
      const rowShift = (progress - 0.5) * tilt;

      lines.push({
        x: centerX - w / 2 + rowShift,
        y: startY + i * (lineHeight + gap),
        width: w,
        height: lineHeight,
        opacity
      });
    }

    return lines;
  };

  const leftEyeLines = generateEyeLines(88, 60, 0);
  const rightEyeLines = generateEyeLines(112, 60, 0);

  // Bouche = un oeil (mêmes dimensions), on garde uniquement les 4 dernières lignes.
  // Cela reproduit exactement la forme "capsule décroissante" du montage.
  const mouthLayout = useMemo(() => {
    const eyeAsMouth = generateEyeLines(100, 79, 0);
    const lowerThree = eyeAsMouth.slice(3); // 3 lignes (on retire la plus haute)
    const step = eyeAsMouth[3].y - eyeAsMouth[2].y; // remonter d'un cran
    return lowerThree.map((line) => ({
      ...line,
      y: line.y - step
    }));
  }, []);

  const mouthPower = useMemo(() => {
    // Niveau global (0..1) : plus réactif pour faire vivre les 3 barres.
    if (!Array.isArray(speechSpectrum) || speechSpectrum.length === 0) return 0;
    let acc = 0;
    let n = 0;
    let peak = 0;
    for (let i = 0; i < Math.min(SPECTRUM_BANDS, speechSpectrum.length); i++) {
      const v = speechSpectrum[i];
      if (typeof v === 'number' && !Number.isNaN(v)) {
        const clamped = Math.min(1, Math.max(0, v));
        acc += clamped;
        if (clamped > peak) peak = clamped;
        n++;
      }
    }
    const avg = n ? acc / n : 0;
    const blended = avg * 0.45 + peak * 0.55;
    return Math.pow(blended, 0.5);
  }, [speechSpectrum]);

  return (
    <div data-testid="robot-avatar" className="relative" style={{ width, height }}>
      {/* Ombre fixe au sol : ne suit pas le flottement du robot */}
      <svg
        viewBox="0 0 200 220"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ width: '100%', height: '100%', overflow: 'visible' }}
      >
        <defs>
          <filter id="groundShadow" x="-60%" y="-140%" width="220%" height="360%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.2" result="shadowBlur" />
            <feColorMatrix
              in="shadowBlur"
              type="matrix"
              values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 0.85 0"
              result="shadowAlpha"
            />
            <feMerge>
              <feMergeNode in="shadowAlpha" />
            </feMerge>
          </filter>
        </defs>
        <ellipse
          cx="100"
          cy="218"
          rx="36"
          ry="6.2"
          fill="rgba(0,0,0,0.22)"
          filter="url(#groundShadow)"
        />
      </svg>

      {/* Robot flottant (idle) */}
      <motion.div
        className="relative"
        style={{ width: '100%', height: '100%' }}
        animate={{ y: [0, -4, 0] }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <svg
          viewBox="0 0 200 220"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          <linearGradient id="bodyGrad3D" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#e2e8f0"/>
            <stop offset="30%" stopColor="#ffffff"/>
            <stop offset="50%" stopColor="#f8fafc"/>
            <stop offset="100%" stopColor="#cbd5e1"/>
          </linearGradient>

          <linearGradient id="whiteGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff"/>
            <stop offset="50%" stopColor="#f1f5f9"/>
            <stop offset="100%" stopColor="#e2e8f0"/>
          </linearGradient>

          <linearGradient id="screenGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#14253f"/>
            <stop offset="100%" stopColor="#0d1a2f"/>
          </linearGradient>
          
          {/* Clip path for screen content */}
          <clipPath id="screenClip">
            <path
              d="M 58 80
                 C 58 92, 74 97, 100 97
                 C 126 97, 142 92, 142 80
                 C 142 70, 136 60, 123 45
                 C 113 35, 87 35, 77 45
                 C 64 60, 58 70, 58 80 Z"
            />
          </clipPath>
        </defs>

        {/* === HEAD (body shape flipped) === */}
        <path
          d="M 50 82
             C 50 94, 66 101, 100 101
             C 134 101, 150 94, 150 82
             C 150 72, 140 55, 123 36
             Q 100 16, 77 36
             C 60 55, 50 72, 50 82 Z"
          fill="url(#whiteGrad)"
        />

        {/* Inner dark screen following the same silhouette */}
        <path
          d="M 56 81
             C 56 93, 72 98, 100 98
             C 128 98, 144 93, 144 81
             C 144 70, 137 60, 124 45
             C 113 35, 87 35, 76 45
             C 63 60, 56 70, 56 81 Z"
          fill="url(#screenGrad)"
        />

        {/* === SCREEN CONTENT (clipped to stay inside) === */}
        <g clipPath="url(#screenClip)" filter="url(#glow)">
          
          {/* Left LED eye */}
          <motion.g
            animate={{ x: isSpeaking ? 0 : [0, 1, -0.6, 0] }}
            transition={{
              x: { duration: 5, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            {leftEyeLines.map((line, i) => (
              <motion.rect
                key={`left-${i}`}
                x={line.x}
                y={line.y}
                width={line.width}
                height={line.height}
                rx="1"
                fill="#3b82f6"
                animate={{ opacity: [line.opacity * 0.85, line.opacity, line.opacity * 0.85] }}
                transition={{ duration: 1.9, repeat: Infinity, delay: i * 0.03 }}
              />
            ))}
          </motion.g>

          {/* Right LED eye */}
          <motion.g
            animate={{ x: isSpeaking ? 0 : [0, 1, -0.6, 0] }}
            transition={{
              x: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.08 }
            }}
          >
            {rightEyeLines.map((line, i) => (
              <motion.rect
                key={`right-${i}`}
                x={line.x}
                y={line.y}
                width={line.width}
                height={line.height}
                rx="1"
                fill="#3b82f6"
                animate={{ opacity: [line.opacity * 0.85, line.opacity, line.opacity * 0.85] }}
                transition={{ duration: 1.9, repeat: Infinity, delay: i * 0.03 + 0.04 }}
              />
            ))}
          </motion.g>

          {/* Blink overlay - original style */}
          {!isSpeaking && (
            <motion.g>
              <motion.rect
                x="74"
                y="50"
                width="28"
                height="18"
                fill="#0d1a2f"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: [0, 0, 0, 0, 0, 1, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  times: [0, 0.7, 0.8, 0.85, 0.9, 0.92, 0.96]
                }}
                style={{ transformOrigin: '88px 59px' }}
              />
              <motion.rect
                x="98"
                y="50"
                width="28"
                height="18"
                fill="#0d1a2f"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: [0, 0, 0, 0, 0, 1, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  times: [0, 0.7, 0.8, 0.85, 0.9, 0.92, 0.96]
                }}
                style={{ transformOrigin: '112px 59px' }}
              />
            </motion.g>
          )}

          {/* Bouche / égaliseur — rotation 180° + fondu entrée / sortie */}
          <AnimatePresence>
            {isSpeaking && (
              <motion.g
                key="robot-mouth"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.38, ease: [0.55, 0.06, 0.68, 0.19] }
                }}
                transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {mouthLayout.map((line, i) => {
                  // Activation progressive par ligne (haut -> bas), pas un simple ON/OFF.
                  const threshold = 0.2 + i * 0.22;
                  const activation = Math.max(0, Math.min(1, (mouthPower - threshold) / 0.28));
                  const minOpacity = 0.12;
                  const targetOpacity = minOpacity + activation * (line.opacity - minOpacity);
                  const isActive = activation > 0.06;
                  return (
                  <motion.rect
                    key={`mouth-${i}`}
                    x={line.x}
                    y={line.y}
                    width={line.width}
                    height={line.height}
                    rx="1"
                    fill={MOUTH_LED}
                    animate={{
                      opacity: isActive
                        ? [targetOpacity * 0.88, targetOpacity, targetOpacity * 0.88]
                        : targetOpacity
                    }}
                    transition={{
                      duration: isActive ? 0.55 : 0.16,
                      repeat: isActive ? Infinity : 0,
                      delay: i * 0.035,
                      ease: "easeInOut"
                    }}
                  />
                  );
                })}
              </motion.g>
            )}
          </AnimatePresence>
        </g>

        {/* === BODY === */}
        <path
          d="M 50 128
             C 50 120, 65 115, 100 115
             C 135 115, 150 120, 150 128
             C 150 135, 140 155, 115 188
             Q 100 202, 85 188
             C 60 155, 50 135, 50 128 Z"
          fill="url(#bodyGrad3D)"
        />
        
        {/* 3D highlight */}
        <path
          d="M 60 130
             C 60 124, 70 120, 88 120
             C 82 145, 78 165, 87 182
             Q 75 170, 68 155
             C 60 140, 60 135, 60 130 Z"
          fill="white"
          opacity="0.4"
        />
        
        {/* Shadow edge */}
        <path
          d="M 140 130
             C 140 124, 130 120, 115 120
             C 120 145, 120 165, 113 182
             Q 125 170, 132 155
             C 140 140, 140 135, 140 130 Z"
          fill="#94a3b8"
          opacity="0.15"
        />

        {/* Arms inspired by EVE (floating in front of body) */}
        <g>
          {/* Left arm */}
          <g transform="translate(40, 140) rotate(24)">
            <motion.ellipse
              cx="0"
              cy="16"
              rx="9"
              ry="24"
              fill="url(#whiteGrad)"
              animate={{ y: [0, -2, 1, 0] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.ellipse
              cx="-2.5"
              cy="8"
              rx="4"
              ry="10"
              fill="white"
              opacity="0.4"
              animate={{ y: [0, -2, 1, 0] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
            />
          </g>

          {/* Right arm */}
          <g transform="translate(160, 140) rotate(-24)">
            <motion.ellipse
              cx="0"
              cy="16"
              rx="9"
              ry="24"
              fill="url(#whiteGrad)"
              animate={{ y: [0, 1, -2, 0] }}
              transition={{ duration: 3.9, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
            />
            <motion.ellipse
              cx="2.5"
              cy="8"
              rx="4"
              ry="10"
              fill="white"
              opacity="0.4"
              animate={{ y: [0, 1, -2, 0] }}
              transition={{ duration: 3.9, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
            />
          </g>
        </g>
        </svg>
      </motion.div>
    </div>
  );
};

export default Robot;
